export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const ALLOWED = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

export function splitDataUrl(dataUrl: string): { base64: string; mediaType: string } {
  const m = dataUrl.match(/^data:([^;]+);base64,(.*)$/);
  if (!m) return { base64: dataUrl, mediaType: 'image/jpeg' };
  const mediaType = ALLOWED.includes(m[1]) ? m[1] : 'image/jpeg';
  return { mediaType, base64: m[2] };
}

// Ước lượng số byte thực của một data URL base64 (bỏ phần header).
export function dataUrlBytes(dataUrl?: string): number {
  if (!dataUrl) return 0;
  const i = dataUrl.indexOf(',');
  const b64 = i >= 0 ? dataUrl.slice(i + 1) : dataUrl;
  const padding = b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0;
  return Math.max(0, Math.floor((b64.length * 3) / 4) - padding);
}

interface CompressOptions {
  maxDim?: number; // cạnh dài tối đa (px), mặc định 1600
  quality?: number; // chất lượng JPEG 0..1, mặc định 0.82
  mimeType?: string; // định dạng đầu ra, mặc định image/jpeg
}

// Nén + resize ảnh trước khi lưu vào IndexedDB để record nhẹ (< ~1MB) mà AI vẫn đọc tốt.
// GIF giữ nguyên (canvas làm mất animation & thường là ảnh nhỏ). Lỗi -> fallback ảnh gốc.
export async function compressImage(file: File, opts: CompressOptions = {}): Promise<string> {
  const { maxDim = 1600, quality = 0.82, mimeType = 'image/jpeg' } = opts;
  const original = await fileToDataUrl(file);
  if (file.type === 'image/gif') return original;

  try {
    const img = await loadImage(original);
    const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
    // Không phóng to; nếu ảnh đã nhỏ và nhẹ (< 900KB) thì giữ nguyên để đỡ tái mã hóa.
    if (scale >= 1 && dataUrlBytes(original) < 900_000) return original;

    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return original;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, w, h);

    const out = canvas.toDataURL(mimeType, quality);
    // Chỉ dùng bản nén nếu thực sự nhẹ hơn bản gốc.
    return dataUrlBytes(out) < dataUrlBytes(original) ? out : original;
  } catch {
    return original; // an toàn: không chặn luồng upload nếu nén lỗi
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
